import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Download, Search, FileText, Camera, ClipboardCheck } from "lucide-react";

const downloadMipDoc = async (doc, API_URL) => {
    const token = localStorage.getItem("trulyn_token");
    const res = await fetch(`${API_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.title.replace(/[^a-zA-Z0-9\s]/g, "").trim()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
};

export default function PortalReports() {
    const { branchParam } = useBranch();
    const [reports, setReports] = useState([]);
    const [mipDocs, setMipDocs] = useState([]);
    const [auditReports, setAuditReports] = useState([]);
    const [q, setQ] = useState("");
    const [tab, setTab] = useState("servicios"); // "servicios" | "mip"
    const [downloading, setDownloading] = useState(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        api.get("/reports", { params: branchParam }).then((r) => setReports(r.data));
        api.get("/photo-reports/client/completed", { params: branchParam })
            .then((r) => setMipDocs(r.data || []))
            .catch(() => {});
        api.get("/audits/client/approved", { params: branchParam }).then((r) => setAuditReports(r.data || [])).catch(() => {});
    }, [branchParam]);

    const filtered = useMemo(() => reports.filter((r) =>
        !q || r.dte_invoice_number?.toLowerCase().includes(q.toLowerCase()) || r.service_date?.includes(q)
    ), [reports, q]);

    const handleMipDownload = async (doc) => {
        setDownloading(doc.service_id);
        try {
            const url = assetUrl(doc.pdf_url);
            window.open(url, "_blank");
        } catch (e) {
            console.error("MIP download error:", e);
            alert("Error al abrir el reporte. Intente nuevamente.");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div data-testid="portal-reports">
            <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Reportes</h1>
            <p className="text-muted-foreground mb-5">Historial completo de inspecciones, tratamientos y reportes MIP.</p>

            {/* Pestañas superiores — siempre visibles */}
            <div className="flex gap-1 mb-5 border-b">
                <button
                    onClick={() => setTab("servicios")}
                    data-testid="tab-servicios"
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        tab === "servicios"
                            ? "border-foreground text-foreground"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                >
                    <FileText className="w-4 h-4" />
                    Reportes de servicio
                    {reports.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1.5">{reports.length}</Badge>
                    )}
                </button>

                <button
                    onClick={() => setTab("mip")}
                    data-testid="tab-mip"
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        tab === "mip"
                            ? "border-foreground text-foreground"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                >
                    <Camera className="w-4 h-4" style={{ color: tab === "mip" ? "#0F766E" : undefined }} />
                    Reportes MIP
                    {mipDocs.length > 0 && (
                        <Badge className="text-[10px] px-1.5" style={{ background: "#F0FDFA", color: "#0F766E", border: "1px solid #99F6E4" }}>
                            {mipDocs.length}
                        </Badge>
                    )}
                </button>

                <button
                    onClick={() => setTab("auditoria")}
                    data-testid="tab-auditoria"
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                        tab === "auditoria"
                            ? "border-foreground text-foreground"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                >
                    <ClipboardCheck className="w-4 h-4" style={{ color: tab === "auditoria" ? "#0F766E" : undefined }} />
                    Reportes de auditoría
                    {auditReports.length > 0 && (
                        <Badge className="text-[10px] px-1.5" style={{ background: "#F0FDFA", color: "#0F766E", border: "1px solid #99F6E4" }}>
                            {auditReports.length}
                        </Badge>
                    )}
                </button>
            </div>

            {/* Contenido: Reportes de servicio */}
            {tab === "servicios" && (
                <>
                    <div className="mb-4 flex gap-2">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Buscar por factura o fecha"
                                className="pl-9"
                                data-testid="portal-reports-search"
                            />
                        </div>
                    </div>
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Factura</TableHead>
                                    <TableHead>Recibido por</TableHead>
                                    <TableHead>Satisfacción</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                                            Sin reportes
                                        </TableCell>
                                    </TableRow>
                                )}
                                {filtered.map((r) => {
                                    const s = r.survey;
                                    const avg = s
                                        ? Math.round(((s.comments_rating + s.punctuality_rating + s.friendliness_rating + s.cordiality_rating + s.efficacy_rating) / 5) * 10) / 10
                                        : null;
                                    return (
                                        <TableRow key={r.id}>
                                            <TableCell className="font-mono">{r.service_date}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono">{r.dte_invoice_number}</Badge>
                                            </TableCell>
                                            <TableCell>{r.accepted_by_name || "—"}</TableCell>
                                            <TableCell>
                                                {avg
                                                    ? <span className="font-mono">{avg}/3</span>
                                                    : <span className="text-muted-foreground">—</span>
                                                }
                                            </TableCell>
                                            <TableCell className="text-right space-x-1">
                                                <Link to={`/portal/reports/${r.id}`}>
                                                    <Button variant="outline" size="sm">
                                                        <FileText className="w-3.5 h-3.5 mr-1" />Ver
                                                    </Button>
                                                </Link>
                                                <a href={assetUrl(r.report_pdf_url)} target="_blank" rel="noreferrer">
                                                    <Button
                                                        size="sm"
                                                        className="bg-[hsl(var(--trulyn-navy))] text-white hover:bg-[hsl(var(--trulyn-navy))]/90"
                                                        data-testid={`portal-download-pdf-${r.id}`}
                                                    >
                                                        <Download className="w-3.5 h-3.5" />
                                                    </Button>
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Card>
                </>
            )}

            {/* Contenido: Reportes MIP */}
            {tab === "mip" && (
                <div data-testid="portal-mip-reports-section">
                    <p className="text-sm text-muted-foreground mb-4">
                        Reportes fotográficos de oportunidades de mejora y acciones correctivas generados automáticamente al finalizar el servicio.
                    </p>
                    {mipDocs.length === 0 ? (
                        <Card className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: "#F0FDFA", border: "1px solid #99F6E4" }}>
                                <Camera className="w-6 h-6" style={{ color: "#0F766E" }} />
                            </div>
                            <p className="font-medium text-sm mb-1">Sin reportes MIP aún</p>
                            <p className="text-xs text-muted-foreground max-w-xs">
                                Los reportes fotográficos aparecerán aquí una vez que el técnico los genere durante el servicio.
                            </p>
                        </Card>
                    ) : (
                        <div className="grid gap-3">
                            {mipDocs.map((doc) => (
                                <Card key={doc.service_id} className="flex items-center gap-3 px-4 py-3" data-testid={`mip-doc-${doc.service_id}`}>
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F0FDFA", border: "1px solid #99F6E4" }}>
                                        <Camera className="w-5 h-5" style={{ color: "#0F766E" }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium leading-snug">
                                            Reporte MIP — Oportunidades de mejora y acciones correctivas
                                            {doc.service_date ? ` (${doc.service_date})` : ""}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {doc.service_date
                                                ? new Date(doc.service_date + "T00:00:00").toLocaleDateString("es-SV", { year: "numeric", month: "long", day: "numeric" })
                                                : ""}
                                        </p>
                                    </div>
                                    <Button
                                        size="sm"
                                        className="shrink-0 gap-1.5"
                                        onClick={() => handleMipDownload(doc)}
                                        disabled={downloading === doc.service_id}
                                        data-testid={`mip-download-${doc.service_id}`}
                                    >
                                        {downloading === doc.service_id
                                            ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Descargando...</>
                                            : <><Download className="w-3.5 h-3.5" />Descargar</>
                                        }
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "auditoria" && (
                <div data-testid="portal-audit-reports">
                    {auditReports.length === 0 ? (
                        <div className="text-center py-16">
                            <ClipboardCheck className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                            <p className="font-medium text-sm mb-1">Sin reportes de auditoría aún</p>
                            <p className="text-xs text-muted-foreground">Aquí verás los reportes fotográficos de las auditorías de calidad aprobados.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {auditReports.map((a) => (
                                <Card key={a.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`audit-report-${a.id}`}>
                                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                                        <ClipboardCheck className="w-4 h-4 text-emerald-700" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">Auditoría de calidad</p>
                                        <p className="text-xs text-muted-foreground">
                                            {a.audit_date ? new Date(a.audit_date + "T00:00:00").toLocaleDateString("es-SV", { year: "numeric", month: "long", day: "numeric" }) : ""}
                                            {a.evaluated_technician_name ? ` · Técnico: ${a.evaluated_technician_name}` : ""}
                                        </p>
                                    </div>
                                    {a.photo_report_pdf_url ? (
                                        <Button size="sm" className="shrink-0 gap-1.5"
                                            onClick={() => window.open(a.photo_report_pdf_url.startsWith("http") ? a.photo_report_pdf_url : assetUrl(a.photo_report_pdf_url), "_blank")}
                                            data-testid={`audit-report-view-${a.id}`}>
                                            <Camera className="w-3.5 h-3.5" /> Ver reporte fotográfico
                                        </Button>
                                    ) : (
                                        <Badge variant="outline" className="text-xs">Reporte en preparación</Badge>
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

        </div>
    );
}
